import React, { useState, useEffect, useCallback } from 'react'
import { Database, RefreshCw, Clock, TrendingUp, Zap, HardDrive, Play } from 'lucide-react'
import { api } from '../lib/api'
import { fmtInt, fmtShort, timeAgo } from '../lib/utils'
import { useSettings } from '../context/SettingsContext'

function StatusRow({ icon: Icon, label, status, detail }) {
  const ok = ['connected', 'running', 'ready', 'active', 'available', true].includes(status)
  const warn = status === 'idle'
  return (
    <div
      className="flex items-center justify-between py-2.5"
      style={{ borderBottom: '1px solid rgba(30,41,59,0.6)' }}
    >
      <div className="flex items-center gap-3">
        <Icon className="h-3.5 w-3.5 text-slate-700 flex-shrink-0" />
        <span className="text-sm text-slate-400 capitalize">{label}</span>
      </div>
      <div className="flex items-center gap-2.5">
        {detail && (
          <span className="text-slate-700 hidden sm:inline" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
            {detail}
          </span>
        )}
        {ok   ? <span className="dot-online"  /> :
         warn ? <span className="dot-warn"    /> :
                <span className="dot-offline" />}
        <span
          className={`font-medium w-16 text-right tracking-wider ${ok ? 'text-emerald-500' : warn ? 'text-amber-500' : 'text-rose-500'}`}
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}
        >
          {ok ? 'ONLINE' : warn ? 'IDLE' : 'OFFLINE'}
        </span>
      </div>
    </div>
  )
}

export default function DataManagement() {
  const { settings } = useSettings()
  const [health,       setHealth]       = useState(null)
  const [dbMetrics,    setDbMetrics]    = useState(null)
  const [loading,      setLoading]      = useState(true)
  const [lastSync,     setLastSync]     = useState(null)
  const [toast,        setToast]        = useState(null)
  const [maintRunning, setMaintRunning] = useState(false)
  const [maintLastRun, setMaintLastRun] = useState(null)
  const [maintResult,  setMaintResult]  = useState(null)

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 4000)
  }

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [h, db] = await Promise.allSettled([api.health(), api.dbMetrics()])
      if (h.status  === 'fulfilled') setHealth(h.value)
      if (db.status === 'fulfilled') setDbMetrics(db.value)
      setLastSync(new Date())
    } catch {}
    setLoading(false)
  }, [])

  const fetchMaintStatus = useCallback(async () => {
    try {
      const d = await api.maintenance.status()
      setMaintRunning(d.running)
      setMaintLastRun(d.last_run)
      setMaintResult(d.last_result)
    } catch {}
  }, [])

  const runMaintenance = async () => {
    if (maintRunning) return
    try {
      const d = await api.maintenance.run()
      if (d.success) {
        setMaintRunning(true)
        showToast('Maintenance started — updating tables and filling data gaps…', 'success')
      } else {
        showToast(d.message || 'Failed to start', 'error')
      }
    } catch {
      showToast('Could not reach backend', 'error')
    }
  }

  useEffect(() => {
    fetchAll()
    fetchMaintStatus()
    const id = setInterval(fetchAll, settings.refreshInterval * 1000)
    return () => clearInterval(id)
  }, [fetchAll, fetchMaintStatus, settings.refreshInterval])

  // Poll maintenance status every 5s while running
  useEffect(() => {
    if (!maintRunning) return
    const id = setInterval(async () => {
      await fetchMaintStatus()
      await fetchAll()   // refresh table counts once done
    }, 5000)
    return () => clearInterval(id)
  }, [maintRunning, fetchMaintStatus, fetchAll])

  const tables      = dbMetrics?.table_counts || {}
  const totalRecs   = dbMetrics?.database_records || 0
  const latestTs    = dbMetrics?.latest_timestamp
  const activity24h = dbMetrics?.recent_activity_24h || 0

  const tableRows = Object.entries(tables)
    .sort(([, a], [, b]) => b - a)
    .map(([name, count]) => ({
      name,
      count,
      pct: totalRecs > 0 ? ((count / totalRecs) * 100).toFixed(1) : '0',
    }))

  return (
    <div className="space-y-5">

      {/* Toast */}
      {toast && (
        <div
          className="fixed top-4 right-4 z-50 px-4 py-3 rounded-lg text-sm font-medium"
          style={{
            background: toast.type === 'success' ? 'rgba(16,185,129,0.15)' :
                        toast.type === 'error'   ? 'rgba(244,63,94,0.15)'  : 'rgba(59,130,246,0.15)',
            border:     toast.type === 'success' ? '1px solid rgba(16,185,129,0.3)' :
                        toast.type === 'error'   ? '1px solid rgba(244,63,94,0.3)'  : '1px solid rgba(59,130,246,0.3)',
            color:      toast.type === 'success' ? '#10b981' :
                        toast.type === 'error'   ? '#f43f5e'  : '#60a5fa',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize:   '0.75rem',
            boxShadow:  '0 4px 20px rgba(0,0,0,0.5)',
          }}
        >
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-slate-700" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.06em' }}>
          {lastSync ? `SYNC ${timeAgo(lastSync)}` : 'LOADING…'}
        </p>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-1.5 border border-gray-700 rounded-lg px-3 py-1.5 text-slate-500 hover:text-slate-300 hover:bg-gray-800 disabled:opacity-40 transition-colors"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em' }}
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          REFRESH
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          {
            label: 'Total Records',
            value: fmtShort(totalRecs),
            sub:   `${tableRows.length} tables`,
            icon:  HardDrive,
            color: '#3b82f6',
          },
          {
            label: 'Latest Bar',
            value: loading ? '…' : latestTs ? timeAgo(latestTs) : 'No data',
            sub:   'Last record timestamp',
            icon:  Clock,
            color: '#f59e0b',
          },
          {
            label: 'Activity 24h',
            value: fmtShort(activity24h),
            sub:   'New bars ingested',
            icon:  TrendingUp,
            color: '#10b981',
          },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div
            key={label}
            className="rounded-xl p-5"
            style={{
              background: '#111827',
              border: '1px solid #1f2937',
              borderTop: `1.5px solid ${color}`,
              boxShadow: `0 -1px 8px ${color}33`,
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-600 uppercase" style={{ fontSize: '0.62rem', letterSpacing: '0.12em', fontWeight: 600 }}>
                  {label}
                </p>
                <p className="font-bold mt-2 tabular-nums" style={{ color, fontFamily: 'JetBrains Mono, monospace', fontSize: '1.5rem' }}>
                  {loading ? '—' : value}
                </p>
                <p className="text-slate-700 mt-1" style={{ fontSize: '0.65rem' }}>{sub}</p>
              </div>
              <Icon className="h-5 w-5 mt-0.5" style={{ color }} />
            </div>
          </div>
        ))}
      </div>

      {/* Table breakdown */}
      <div className="rounded-xl border border-gray-800 overflow-hidden" style={{ background: '#111827' }}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #1f2937' }}>
          <div>
            <h3 className="section-header" style={{ marginBottom: '0.2rem' }}>Database Tables</h3>
            <p className="text-slate-700" style={{ fontSize: '0.65rem', fontFamily: 'JetBrains Mono, monospace' }}>
              TimescaleDB — row counts via direct COUNT(*) per table
            </p>
          </div>
          <Database className="h-4 w-4 text-slate-700" />
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-700">
            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
            <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>LOADING…</p>
          </div>
        ) : tableRows.length === 0 ? (
          <div className="p-8 text-center text-slate-700 text-sm">No table data available</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: '#0f172a' }}>
                <tr>
                  {['Table', 'Records', '% of Total', 'Distribution'].map((h, i) => (
                    <th
                      key={h}
                      className={`px-4 py-3 ${i === 1 || i === 2 ? 'text-right' : 'text-left'}`}
                      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#4b5563', fontWeight: 600, textTransform: 'uppercase' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, i) => (
                  <tr
                    key={i}
                    style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }}
                    className="hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-4 py-2.5 text-slate-400" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}>
                      {row.name}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-slate-300 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                      {fmtInt(row.count)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}>
                      {row.pct}%
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="h-1.5 rounded-full overflow-hidden w-32" style={{ background: '#1e293b' }}>
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${row.pct}%`, background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #1e293b', background: '#0f172a' }}>
                  <td className="px-4 py-3 font-semibold text-slate-400" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}>
                    TOTAL
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-slate-200 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                    {fmtInt(totalRecs)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Service status + retention policy */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Service status */}
        <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
          <h3 className="section-header">Service Status</h3>
          <StatusRow icon={Zap}      label="Backend API"     status={health ? 'running' : 'error'}            detail={health?.framework} />
          <StatusRow icon={Database} label="Database Access" status={health?.database_access || 'unavailable'} detail={latestTs ? timeAgo(latestTs) : undefined} />
          <StatusRow icon={TrendingUp} label="Data Collection" status={activity24h > 0 ? 'active' : 'idle'}    detail={`${fmtShort(activity24h)} bars/24h`} />
          <StatusRow icon={Clock}    label="Signal Scanner"  status={health ? 'ready' : 'error'}              detail={health?.rotation_groups ? `Every ${health.rotation_groups} min` : '—'} />
        </div>

        {/* Retention policy */}
        <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
          <h3 className="section-header">Data Retention Policy</h3>
          <div className="space-y-0">
            {[
              ['tactical_bars_realtime', '1 day',   'Current session 1-min bars'],
              ['tactical_bars_90day',    '90 days',  'Rolling 1-min OHLCV'],
              ['tactical_bars_daily',    '1 year',   'Daily bars'],
              ['active_signals',         'live',     `Primary signal table (${tables.active_signals != null ? fmtInt(tables.active_signals) : '—'} rows)`],
              ['logic_gate_decisions',   'live',     'Decision engine log'],
              ['position_tracking',      'live',     'Open position watermarks'],
              ['error_log',              '90 days',  'System errors'],
            ].map(([table, retention, desc]) => (
              <div
                key={table}
                className="flex items-center justify-between py-2.5"
                style={{ borderBottom: '1px solid rgba(30,41,59,0.5)' }}
              >
                <div>
                  <p className="text-slate-400" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
                    {table}
                  </p>
                  <p className="text-slate-700 mt-0.5" style={{ fontSize: '0.65rem' }}>{desc}</p>
                </div>
                <span
                  className="text-slate-500 px-2 py-0.5 rounded"
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '0.65rem',
                    background: '#0f172a',
                    border: '1px solid #1e293b',
                  }}
                >
                  {retention}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Maintenance */}
      <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="section-header" style={{ marginBottom: '0.2rem' }}>Maintenance</h3>
            <p className="text-slate-700" style={{ fontSize: '0.65rem', fontFamily: 'JetBrains Mono, monospace' }}>
              Runs automatically at 4:30 PM ET daily · Aggregates daily bars · Fills data gaps · Applies retention
            </p>
          </div>
          {maintLastRun && (
            <span className="text-slate-700 hidden sm:block" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>
              Last run {timeAgo(maintLastRun)}
              {maintResult && maintResult !== 'running' && (
                <span className={maintResult === 'completed' ? ' text-emerald-700' : ' text-rose-700'}>
                  {' '}· {maintResult.toUpperCase()}
                </span>
              )}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={runMaintenance}
            disabled={maintRunning}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
            style={{
              background: maintRunning ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.15)',
              border: '1px solid rgba(16,185,129,0.4)',
              color: '#10b981',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '0.72rem',
              letterSpacing: '0.06em',
            }}
          >
            {maintRunning
              ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              : <Play className="h-3.5 w-3.5" />
            }
            {maintRunning ? 'RUNNING…' : 'UPDATE NOW'}
          </button>

          <button
            onClick={() => { fetchAll(); showToast('Stats refreshed', 'success') }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors hover:bg-gray-800"
            style={{ color: '#64748b', border: '1px solid #374151', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem', letterSpacing: '0.06em' }}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            REFRESH STATS
          </button>
        </div>

        {maintRunning && (
          <p className="mt-3 text-emerald-700" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
            Running: aggregating daily bars, checking all symbols for gaps, backfilling via Alpaca IEX, applying retention policies…
          </p>
        )}
      </div>
    </div>
  )
}
